import { c as _c } from "react/compiler-runtime";
import type { ParsedUrlQuery } from 'node:querystring';
import { getSdkArgsToken, hasInvalidHashFragment } from '@onefootprint/core';
import { getOrgSdkArgsOptions } from '@onefootprint/fetch-hosted/queries';
import { logClientVersion } from '@onefootprint/idv';
import { useFootprintProvider } from '@onefootprint/idv2';
import type { DocumentFixtureResult, Language, Locale, VerifyV1SdkArgs, WorkflowFixtureResult } from '@onefootprint/request-types';
import { useQuery } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';

// See documentation:
// https://www.notion.so/onefootprint/SDK-Bifrost-Communication-e5fa05ddbfb34dc593b3e58a1398ad1c

type UsePropsProps = {
  onSuccess: (props: VerifyV1SdkArgs) => void;
  onError: (error: unknown) => void;
};

// Different SDK packages have different version thresholds for not requiring SDK args
const isRequiredToFetchSdkArgs = (sdkVersion?: string) => {
  if (!sdkVersion) {
    return true;
  }

  // All packages use npm scoped format: @onefootprint/package-name:version
  if (!sdkVersion.startsWith('@onefootprint/') || !sdkVersion.includes(':')) {
    return true;
  }
  const colonIndex = sdkVersion.lastIndexOf(':');
  const packageName = sdkVersion.substring(0, colonIndex);
  const version = sdkVersion.substring(colonIndex + 1);
  const majorVersion = Number(version.split('.')[0]);
  if (Number.isNaN(majorVersion)) {
    return true;
  }

  // Different packages have different version thresholds
  switch (packageName) {
    case '@onefootprint/footprint-expo':
    case '@onefootprint/footprint-react-native':
      return majorVersion < 3;
    case '@onefootprint/footprint-js':
      return majorVersion < 5;
    default:
      return true;
  }
};

// Helper function to check if version string has package name in npm format
const hasPackageName = (versionString: string): boolean => {
  // All packages use npm scoped format: @onefootprint/package-name:version
  return versionString.startsWith('@onefootprint/') && versionString.includes(':');
};

// Get SDK version from search params first, fallback to fpProvider data
const getSdkVersion = (queryParams: QueryParams, fpProviderSdkVersion?: string): string | undefined => {
  // If query param is provided and has package name, use it
  if (queryParams.sdk_version && hasPackageName(queryParams.sdk_version)) {
    return queryParams.sdk_version;
  }

  // If query param is provided but doesn't have package name, don't fallback (it's explicitly set but invalid)
  if (queryParams.sdk_version) {
    return undefined;
  }

  // No query param, check fpProvider version
  if (fpProviderSdkVersion && hasPackageName(fpProviderSdkVersion)) {
    return fpProviderSdkVersion;
  }
  return undefined;
};
type QueryParams = {
  document_outcome?: DocumentFixtureResult;
  lng?: Language;
  locale?: Locale;
  overall_outcome?: WorkflowFixtureResult;
  variables?: string;
  variant?: string;
  xfpsessionid?: string;
  public_key?: string;
  sandbox_id?: string;
  auth_token?: string;
  is_components_sdk?: string;
  should_relay_to_components?: string;
  sdk_version?: string;
};
const getPublicKeyFromUrl = (query: ParsedUrlQuery): string | undefined => {
  const tenantPk = query.public_key;
  if (!tenantPk) {
    return undefined;
  }
  if (typeof tenantPk === 'string') {
    return tenantPk;
  }
  return tenantPk[0];
};
const parseLocale = (value: unknown): Locale | undefined => {
  if (typeof value !== 'string') return undefined;
  const language = value.toLowerCase().split('-')[0];
  if (language === 'en') return 'en-US';
  if (language === 'es') return 'es-MX';
  return undefined;
};
const urlToVerifyV1SdkArgs = (query: QueryParams, authToken?: string): VerifyV1SdkArgs => {
  return {
    authToken: authToken,
    documentFixtureResult: query.document_outcome,
    fixtureResult: query.overall_outcome,
    sandboxId: query.sandbox_id,
    l10n: {
      language: query.lng,
      locale: parseLocale(query.locale)
    },
    publicKey: getPublicKeyFromUrl(query),
    isComponentsSdk: query.is_components_sdk === 'true',
    shouldRelayToComponents: query.should_relay_to_components === 'true'
  };
};
const useProps = t0 => {
  const $ = _c(31);
  const {
    onSuccess,
    onError
  } = t0;
  const fpProvider = useFootprintProvider();
  const router = useRouter();
  const onSuccessCalled = useRef(false);
  const sdkArgsToken = getSdkArgsToken(router.asPath.split("#")[1] ?? router.query.auth_token ?? "") || undefined;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["fp-provider"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== fpProvider) {
    t2 = {
      queryKey: t1,
      queryFn: () => fpProvider.load().then(_temp)
    };
    $[1] = fpProvider;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    data: fpProviderData,
    isLoading: isFpProviderLoading,
    isSuccess: isFpProviderSuccess
  } = useQuery(t2);
  const currentSdkVersion = getSdkVersion(router.query as QueryParams, fpProviderData?.model?.sdkVersion);
  const shouldFetchSdkArgs = Boolean((router.query.sdk_version || !isFpProviderLoading) && isRequiredToFetchSdkArgs(currentSdkVersion) && !!sdkArgsToken);
  const t3 = getOrgSdkArgsOptions({
    headers: {
      "X-Fp-Sdk-Args-Token": sdkArgsToken
    }
  });
  let t4;
  if ($[3] !== shouldFetchSdkArgs || $[4] !== t3) {
    t4 = {
      ...t3,
      enabled: shouldFetchSdkArgs
    };
    $[3] = shouldFetchSdkArgs;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const {
    data: sdkArgsData,
    isError: isSdkArgsError,
    error: sdkArgsError
  } = useQuery(t4);
  let t5;
  if ($[6] !== onSuccess) {
    t5 = props => {
      if (onSuccessCalled.current) {
        return;
      }
      onSuccessCalled.current = true;
      onSuccess(props);
    };
    $[6] = onSuccess;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const complete = t5;
  let t6;
  let t7;
  if ($[8] !== isSdkArgsError || $[9] !== onError || $[10] !== sdkArgsError) {
    t6 = () => {
      if (isSdkArgsError) {
        onError(sdkArgsError);
      }
    };
    t7 = [isSdkArgsError, onError, sdkArgsError];
    $[8] = isSdkArgsError;
    $[9] = onError;
    $[10] = sdkArgsError;
    $[11] = t6;
    $[12] = t7;
  } else {
    t6 = $[11];
    t7 = $[12];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[13] !== onError || $[14] !== router.asPath) {
    t8 = () => {
      if (hasInvalidHashFragment(router.asPath)) {
        onError(new TypeError("Invalid URL fragment"));
      }
    };
    t9 = [onError, router.asPath];
    $[13] = onError;
    $[14] = router.asPath;
    $[15] = t8;
    $[16] = t9;
  } else {
    t8 = $[15];
    t9 = $[16];
  }
  useEffect(t8, t9);
  let t10;
  let t11;
  if ($[17] !== complete || $[18] !== sdkArgsData) {
    t10 = () => {
      if (sdkArgsData) {
        complete(sdkArgsData.args.data as VerifyV1SdkArgs);
      }
    };
    t11 = [complete, sdkArgsData];
    $[17] = complete;
    $[18] = sdkArgsData;
    $[19] = t10;
    $[20] = t11;
  } else {
    t10 = $[19];
    t11 = $[20];
  }
  useEffect(t10, t11);
  let t12;
  let t13;
  if ($[21] !== router.query.sdk_version) {
    t12 = () => {
      if (typeof router.query.sdk_version === "string") {
        logClientVersion(router.query.sdk_version);
      }
    };
    t13 = [router.query.sdk_version];
    $[21] = router.query.sdk_version;
    $[22] = t12;
    $[23] = t13;
  } else {
    t12 = $[22];
    t13 = $[23];
  }
  useEffect(t12, t13);
  let t14;
  let t15;
  if ($[24] !== complete || $[25] !== isFpProviderSuccess || $[26] !== router.query || $[27] !== sdkArgsToken || $[28] !== shouldFetchSdkArgs) {
    t14 = () => {
      const canComplete = router.query.sdk_version ? true : isFpProviderSuccess;
      if (canComplete && !shouldFetchSdkArgs) {
        complete(urlToVerifyV1SdkArgs(router.query, sdkArgsToken));
      }
    };
    t15 = [complete, isFpProviderSuccess, router.query, sdkArgsToken, shouldFetchSdkArgs];
    $[24] = complete;
    $[25] = isFpProviderSuccess;
    $[26] = router.query;
    $[27] = sdkArgsToken;
    $[28] = shouldFetchSdkArgs;
    $[29] = t14;
    $[30] = t15;
  } else {
    t14 = $[29];
    t15 = $[30];
  }
  useEffect(t14, t15);
};
export type { QueryParams };
export { getSdkVersion, isRequiredToFetchSdkArgs };
export default useProps;
function _temp(data) {
  logClientVersion(data?.model?.sdkVersion);
  return data ?? null;
}